import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Mountains, List, X, ShoppingBag, UserCircle, Globe, CurrencyCircleDollar, CaretDown } from "@phosphor-icons/react";

const STORE_CATEGORIES = [
  { slug: "all", labels: { es: "Todos los productos", en: "All products", pt: "Todos os produtos", fr: "Tous les produits" } },
  { slug: "zapatillas", labels: { es: "Zapatillas", en: "Shoes", pt: "Calcados", fr: "Chaussures" } },
  { slug: "ropa", labels: { es: "Ropa", en: "Clothing", pt: "Roupas", fr: "Vetements" } },
  { slug: "equipamiento", labels: { es: "Equipamiento", en: "Equipment", pt: "Equipamento", fr: "Equipement" } },
  { slug: "accesorios", labels: { es: "Accesorios", en: "Accessories", pt: "Acessorios", fr: "Accessoires" } },
];

const CART_KEY = "provertical-cart";
function readCartCount() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return arr.reduce((acc, it) => acc + (it.quantity || 0), 0);
  } catch { return 0; }
}

// Mini icono de carrito con contador. Refresca al storage event + cada 1.5s (cubre updates same-tab).
function CartIconLink({ className = "" }) {
  const [count, setCount] = useState(readCartCount());
  useEffect(() => {
    const refresh = () => setCount(readCartCount());
    refresh();
    window.addEventListener("storage", refresh);
    const t = setInterval(refresh, 1500);
    return () => { window.removeEventListener("storage", refresh); clearInterval(t); };
  }, []);
  return (
    <Link to="/tienda?openCart=1" className={`relative inline-flex items-center justify-center ${className}`} data-testid="nav-cart-btn" aria-label="Carrito">
      <ShoppingBag size={22} className="text-gray-300 hover:text-[#FF4D00] transition-colors" />
      {count > 0 && (
        <span className="absolute -top-1.5 -right-1.5 bg-[#FF4D00] text-white text-[10px] font-black h-4 min-w-[1rem] px-1 rounded-full flex items-center justify-center" data-testid="nav-cart-badge">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const { lang, changeLang, t, languages } = useTranslation();
  const { currency, changeCurrency, currencies } = useCurrency();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) return null;

  const NAV_LINKS = [
    { to: "/eventos", label: t("nav_events") },
    { to: "/entrenamientos", label: t("nav_training") },
    { to: "/tienda", label: t("nav_store"), hasSubmenu: true },
    { to: "/galeria", label: lang === "en" ? "Gallery" : lang === "fr" ? "Galerie" : "Galeria" },
    { to: "/nosotros", label: lang === "en" ? "About Us" : lang === "fr" ? "A propos" : "Pro Vertical" },
    { to: "/contacto", label: t("nav_contact") },
  ];

  const storeCatLabel = (cat) => cat.labels[lang] || cat.labels.es;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <Mountains size={28} weight="bold" className="text-[#FF4D00]" />
          <span className="font-['Cabinet_Grotesk'] font-black text-lg uppercase tracking-tight">Pro Vertical</span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((link) => link.hasSubmenu ? (
            <DropdownMenu key={link.to}>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`nav-link-${link.to.slice(1)}`}
                  className={`text-sm font-medium transition-colors hover:text-[#FF4D00] inline-flex items-center gap-1 ${location.pathname === link.to ? "text-[#FF4D00]" : "text-gray-300"}`}>
                  {link.label} <CaretDown size={12} weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[#121212] border-white/10 min-w-[200px]">
                {STORE_CATEGORIES.map((cat) => (
                  <DropdownMenuItem key={cat.slug} asChild>
                    <Link
                      to={cat.slug === "all" ? "/tienda" : `/tienda?cat=${cat.slug}`}
                      className="cursor-pointer w-full"
                      data-testid={`nav-store-cat-${cat.slug}`}>
                      {storeCatLabel(cat)}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link key={link.to} to={link.to} data-testid={`nav-link-${link.to.slice(1)}`}
              className={`text-sm font-medium transition-colors hover:text-[#FF4D00] ${location.pathname === link.to ? "text-[#FF4D00]" : "text-gray-300"}`}>
              {link.label}
            </Link>
          ))}

          <CartIconLink />

          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white gap-1 px-2" data-testid="lang-selector">
                <Globe size={16} /> {lang.toUpperCase()}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#121212] border-white/10">
              {languages.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => changeLang(l.code)}
                  className={`cursor-pointer ${lang === l.code ? "text-[#FF4D00]" : ""}`}
                  data-testid={`lang-option-${l.code}`}>
                  <span className="font-mono text-xs mr-2 w-6">{l.flag}</span> {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Currency Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white gap-1 px-2" data-testid="currency-selector">
                <CurrencyCircleDollar size={16} /> {currency.code}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#121212] border-white/10">
              {currencies.map((c) => (
                <DropdownMenuItem key={c.code} onClick={() => changeCurrency(c.code)}
                  className={`cursor-pointer ${currency.code === c.code ? "text-[#FF4D00]" : ""}`}
                  data-testid={`currency-option-${c.code}`}>
                  <span className="font-mono text-xs mr-2 w-8">{c.symbol}</span> {c.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <Link to="/admin">
              <Button variant="outline" size="sm" className="border-[#FF4D00]/30 text-[#FF4D00] hover:bg-[#FF4D00]/10" data-testid="nav-admin-btn">
                <UserCircle size={16} className="mr-1" /> {t("nav_admin")}
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white" data-testid="nav-login-btn">
                {t("nav_login")}
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile: cart + toggle */}
        <div className="md:hidden flex items-center gap-3">
          <CartIconLink />
          <button className="text-white" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0a0a0a] border-t border-white/10 px-6 py-4 space-y-3" data-testid="mobile-menu">
          {NAV_LINKS.map((link) => link.hasSubmenu ? (
            <div key={link.to}>
              <Link to={link.to} onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-gray-300 hover:text-[#FF4D00] py-2">{link.label}</Link>
              <div className="pl-4 space-y-1 border-l border-white/10 ml-2">
                {STORE_CATEGORIES.filter(c => c.slug !== "all").map((cat) => (
                  <Link key={cat.slug} to={`/tienda?cat=${cat.slug}`} onClick={() => setMobileOpen(false)}
                    className="block text-xs text-gray-400 hover:text-[#FF4D00] py-1" data-testid={`mobile-nav-store-cat-${cat.slug}`}>
                    {storeCatLabel(cat)}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
              className="block text-sm font-medium text-gray-300 hover:text-[#FF4D00] py-2">{link.label}</Link>
          ))}
          <div className="flex items-center gap-4 pt-2 border-t border-white/10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-gray-300 gap-1 px-2"><Globe size={16} /> {lang.toUpperCase()}</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#121212] border-white/10">
                {languages.map((l) => (
                  <DropdownMenuItem key={l.code} onClick={() => changeLang(l.code)} className={lang === l.code ? "text-[#FF4D00]" : ""}>
                    {l.flag} {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-gray-300 gap-1 px-2"><CurrencyCircleDollar size={16} /> {currency.code}</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#121212] border-white/10">
                {currencies.map((c) => (
                  <DropdownMenuItem key={c.code} onClick={() => changeCurrency(c.code)} className={currency.code === c.code ? "text-[#FF4D00]" : ""}>
                    {c.symbol} {c.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Link to={user ? "/admin" : "/login"} onClick={() => setMobileOpen(false)}
            className="block text-sm font-medium text-[#FF4D00] py-2">{user ? t("nav_admin") : t("nav_login")}</Link>
        </div>
      )}
    </nav>
  );
}
